import { AnyAction } from 'redux';
import { TargetNode, TargetSummary } from '../models/TargetNode';
import {
  SELECT_NODE,
  TOGGLE_NODE,
  DESELECT_NODE,
  EDIT_NODE,
  SET_CUSTOM_PICTURE,
  SET_TOGGLED,
  REQUEST_TREE,
  REQUEST_TARGET,
  REQUEST_TREE_ERROR,
  REQUEST_TARGET_ERROR,
  RECEIVE_TREE_SUCCESS,
  RECEIVE_TARGET_SUCCESS,
  PARSE_TARGETS,
  SAVE_TARGET,
  SAVE_TARGET_ERROR,
  SAVE_TARGET_SUCCESS,
  REQUEST_DELETE_TARGET,
  REQUEST_DELETE_TARGET_SUCCESS,
  REQUEST_DELETE_TARGET_ERROR,
  REQUEST_DELETE_TARGET_FEEDBACK,
  REQUEST_DELETE_TARGET_FEEDBACK_SUCCESS,
  REQUEST_DELETE_TARGET_FEEDBACK_ERROR,
  FILTER_TREE,
  RESET_TREE,
  SHOW_DISABLED_TARGETS
} from '../constants/treeActions';

// Plain-object state (de-Immutabled — track2 Phase 4). Was Immutable.fromJS(...).
export interface TreeState {
  /** The target currently open in the editor. `{}` before one is selected. */
  selectedNode: TargetNode;
  /** The whole target tree. `{}` before it loads — NodeTree relies on that. */
  tree: TargetNode;
  isTreeLoading: boolean;
  isTargetLoading: boolean;
  isTargetSaving: boolean;
  isTargetDeleting: boolean;
  error: boolean;
  errorMessage: string;
  /** Flat list built by the parseTargets thunk, for the editor's dropdowns. */
  targets: TargetSummary[];
  /** Ids of expanded tree nodes. */
  toggled: string[];
  showDisabledTargets: boolean;
}

const initialState: TreeState = {
  selectedNode: {},
  tree: {},
  isTreeLoading: false,
  isTargetLoading: false,
  isTargetSaving: false,
  isTargetDeleting: false,
  error: false,
  errorMessage: '',
  targets: [],
  toggled: [],
  showDisabledTargets: false
};

// Immutable deep-set for plain objects/arrays (replaces Immutable's setIn):
// returns a fresh structure along `path`, leaving the input untouched. Numeric
// array indices arrive as strings (keyPath came from a '.'-split) — bracket
// assignment on the copied array handles that.
function setInPlain(obj: any, path: Array<string | number>, value: unknown): any {
  if (path.length === 0) {
    return value;
  }
  const [key, ...rest] = path;
  const container = Array.isArray(obj) ? obj.slice() : { ...obj };
  container[key] = setInPlain(obj == null ? undefined : obj[key], rest, value);
  return container;
}

export default function reducer(state: TreeState = initialState, action: AnyAction): TreeState {
  switch (action.type) {
    case SELECT_NODE:
      return { ...state, selectedNode: action.payload };
    case DESELECT_NODE:
      return setInPlain(state, action.payload.keyPath, action.payload.node);
    case TOGGLE_NODE:
      return setInPlain(state, action.payload.keyPath, action.payload.node);
    case EDIT_NODE: {
      // Trap: return the SAME state ref when the value is unchanged. Immutable's
      // setIn did this for free; plain spreads always make a new object, which
      // would re-render useSelector and re-fire the qrcode self-limiting effect
      // in SelectedNode -> infinite loop. Guard on strict equality.
      const { property, value } = action.payload;
      if (state.selectedNode[property as string] === value) {
        return state;
      }
      return {
        ...state,
        selectedNode: { ...state.selectedNode, [property]: value }
      };
    }
    case SET_CUSTOM_PICTURE: {
      // Trap: build a FRESH array (was Immutable List.push). A bare .push would
      // mutate in place and not re-render.
      const customizations = state.selectedNode.customizations || {};
      const picture = customizations.picture || [];
      return {
        ...state,
        selectedNode: {
          ...state.selectedNode,
          customizations: {
            ...customizations,
            picture: [...picture, action.payload]
          }
        }
      };
    }
    case SET_TOGGLED:
      return { ...state, toggled: action.payload };
    case REQUEST_TREE:
      return { ...state, isTreeLoading: true, error: false, errorMessage: '' };
    case REQUEST_TREE_ERROR:
      return {
        ...state,
        isTreeLoading: false,
        error: true,
        errorMessage: action.payload.errorMessage
      };
    case RECEIVE_TREE_SUCCESS:
      return {
        ...state,
        isTreeLoading: false,
        error: false,
        errorMessage: '',
        tree: action.payload
      };
    case REQUEST_TARGET:
      return { ...state, isTargetLoading: true, error: false, errorMessage: '' };
    case REQUEST_TARGET_ERROR:
      return {
        ...state,
        isTargetLoading: false,
        error: true,
        errorMessage: action.payload.errorMessage
      };
    case RECEIVE_TARGET_SUCCESS:
      return {
        ...state,
        isTargetLoading: false,
        error: false,
        errorMessage: '',
        selectedNode: action.payload
      };
    case PARSE_TARGETS:
      return { ...state, targets: action.payload };
    case SAVE_TARGET:
      return { ...state, isTargetSaving: true, error: false, errorMessage: '' };
    case SAVE_TARGET_ERROR:
      return { ...state, isTargetSaving: false };
    case SAVE_TARGET_SUCCESS:
      return { ...state, isTargetSaving: false, error: false, errorMessage: '' };
    case REQUEST_DELETE_TARGET:
      return { ...state, isTargetDeleting: true, error: false, errorMessage: '' };
    case REQUEST_DELETE_TARGET_SUCCESS:
      return { ...state, isTargetDeleting: false, error: false, errorMessage: '' };
    case REQUEST_DELETE_TARGET_ERROR:
      return {
        ...state,
        isTargetDeleting: false,
        error: true,
        errorMessage: action.payload.errorMessage || '_delete_target_error_'
      };
    case REQUEST_DELETE_TARGET_FEEDBACK:
      return { ...state, isTargetDeleting: true, error: false, errorMessage: '' };
    case REQUEST_DELETE_TARGET_FEEDBACK_SUCCESS:
      return { ...state, isTargetDeleting: false, error: false, errorMessage: '' };
    case REQUEST_DELETE_TARGET_FEEDBACK_ERROR:
      return {
        ...state,
        isTargetDeleting: false,
        error: true,
        errorMessage: action.payload.errorMessage || '_delete_target_feedback_error_'
      };
    case FILTER_TREE:
      return { ...state, tree: action.payload };
    case RESET_TREE:
      return initialState;
    case SHOW_DISABLED_TARGETS:
      return { ...state, showDisabledTargets: action.payload };
    default:
      // console.debug('*** tree reducer default:[%O]', action)
      return state;
  }
}
